import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

function AccountTable({ users, onEdit, onDelete, canDeleteUsersOnly }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderDirection, setOrderDirection] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getRoleValue = (user) => {
    if (user.superadmin) return 1;
    if (user.admin) return 2;
    if (user.staff) return 3;
    if (user.user) return 4;
    if (user.resident) return 5;
    return 5;
  };

  const sortedUsers = () => {
    return users.sort((a, b) => {
      let aValue = orderBy === 'role' ? getRoleValue(a) : a[orderBy];
      let bValue = orderBy === 'role' ? getRoleValue(b) : b[orderBy];

      if (aValue < bValue) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [showAll, setShowAll] = useState({});

  return (
    <TableContainer component={Paper}>
      <Table aria-label="user table">
        <TableHead>
          <TableRow>
            <TableCell key="edit"></TableCell>

            <TableCell key="firstName">
              <TableSortLabel
                active={orderBy === 'firstName'}
                direction={orderBy === 'firstName' ? orderDirection : 'asc'}
                onClick={() => handleRequestSort('firstName')}
              >
                First Name
              </TableSortLabel>
            </TableCell>

            <TableCell key="lastName">
              <TableSortLabel
                active={orderBy === 'lastName'}
                direction={orderBy === 'lastName' ? orderDirection : 'asc'}
                onClick={() => handleRequestSort('lastName')}
              >
                Last Name
              </TableSortLabel>
            </TableCell>

            <TableCell key="email">
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? orderDirection : 'asc'}
                onClick={() => handleRequestSort('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell key="role">
              <TableSortLabel
                active={orderBy === 'role'}
                direction={orderBy === 'role' ? orderDirection : 'asc'}
                onClick={() => handleRequestSort('role')}
              >
                Role
              </TableSortLabel>
            </TableCell>
            <TableCell key="facilities">Facilities</TableCell>
            <TableCell key="assignedResidents">Assigned Residents</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers()
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((user) => (
              <TableRow key={user.email}>
                <TableCell>
                  <div style={{ display: 'inline-flex' }}>
                    <EditIcon
                      onClick={() => onEdit(user)}
                      style={{ cursor: 'pointer' }}
                    />
                    {(!canDeleteUsersOnly ||
                      (!user.superadmin && !user.admin)) && (
                      <DeleteForeverIcon
                        onClick={() => onDelete(user)}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell component="th" scope="row">
                  {user.lastName}
                </TableCell>
                <TableCell component="th" scope="row">
                  {user.firstName}
                </TableCell>
                <TableCell component="th" scope="row">
                  {user.email}
                </TableCell>
                <TableCell>
                  {user.superadmin
                    ? 'Superadmin'
                    : user.admin
                      ? 'Admin'
                      : user.staff
                        ? 'Staff'
                        : user.resident
                          ? 'Resident'
                          : user.user
                            ? 'User'
                            : 'Unknown'}
                </TableCell>
                <TableCell>
                  {showAll[user.id]
                    ? user.facilities.map((f) => f?.name).join(', ')
                    : user.facilities
                        .slice(0, 4)
                        .map((f) => f?.name)
                        .join(', ')}
                  {user.facilities.length > 4 && !showAll[user.id] && (
                    <span
                      onClick={() =>
                        setShowAll((prev) => ({ ...prev, [user.id]: true }))
                      }
                      style={{
                        color: 'blue',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        marginLeft: '5px',
                      }}
                    >
                      Show More
                    </span>
                  )}
                  {showAll[user.id] && (
                    <span
                      onClick={() =>
                        setShowAll((prev) => ({ ...prev, [user.id]: false }))
                      }
                      style={{
                        color: 'blue',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        marginLeft: '5px',
                      }}
                    >
                      Show Less
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {user.assignedResidents
                    .map((f) => f?.residentName)
                    .join(', ')}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
}

AccountTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      admin: PropTypes.bool,
      superadmin: PropTypes.bool,
      facilities: PropTypes.array,
      assignedResidents: PropTypes.array,
    }),
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canDeleteUsersOnly: PropTypes.bool.isRequired,
};

export default AccountTable;
